<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveFrontend">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Edit frontend') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            class="btn btn-red shadow-sm rounded-md"
                            type="button"
                            @click="deleteFrontendModal = true"
                        >
                            {{ $t('Delete frontend') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <!-- General -->
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Frontend details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Frontend name, URL and first receiver settings') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="name"
                                            v-model="frontend.name"
                                            :placeholder="$t('Name')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="url">{{ $t('URL') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="url"
                                            v-model="frontend.url"
                                            placeholder="https://support.example.com"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="first_receiver_id">{{ $t('First receiver') }}</label>
                                    <select
                                        id="first_receiver_id"
                                        v-model="frontend.first_receiver_id"
                                        class="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 mt-1"
                                    >
                                        <option :value="null">{{ $t('None') }}</option>
                                        <option v-for="employee in employees" :value="employee.id">{{ employee.name }} ({{ employee.email }})</option>
                                    </select>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        {{ $t('Employee who receives all new tickets from this frontend first') }}.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- API Key -->
                        <div class="md:col-span-3">
                            <div class="py-3">
                                <div class="border-t border-gray-200"></div>
                            </div>
                        </div>
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('API Key') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Unique API key for this frontend') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <div class="flex items-center">
                                        <code class="flex-1 text-xs bg-gray-100 px-3 py-2 rounded break-all select-all">{{ frontend.api_key }}</code>
                                        <button
                                            class="btn btn-secondary shadow-sm rounded-md ml-4"
                                            type="button"
                                            @click="regenerateApiKey"
                                        >
                                            {{ $t('Regenerate') }}
                                        </button>
                                    </div>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        {{ $t('This key is used by the frontend to authenticate API requests') }}.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- SMTP -->
                        <div class="md:col-span-3">
                            <div class="py-3">
                                <div class="border-t border-gray-200"></div>
                            </div>
                        </div>
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('SMTP settings') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Optional SMTP configuration for this frontend') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="smtp_email">{{ $t('SMTP Email') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="smtp_email"
                                            v-model="frontend.smtp_email"
                                            placeholder="noreply@example.com"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="sender_name">{{ $t('Sender name') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="sender_name"
                                            v-model="frontend.sender_name"
                                            :placeholder="$t('Sender name')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="smtp_server">{{ $t('SMTP Server') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="smtp_server"
                                            v-model="frontend.smtp_server"
                                            placeholder="smtp.example.com"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-2">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="smtp_port">{{ $t('SMTP Port') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="smtp_port"
                                            v-model="frontend.smtp_port"
                                            type="number"
                                            placeholder="587"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-1">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="smtp_encryption">{{ $t('Encryption') }}</label>
                                    <select
                                        id="smtp_encryption"
                                        v-model="frontend.smtp_encryption"
                                        class="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 mt-1"
                                    >
                                        <option value="NO">NO</option>
                                        <option value="SSL">SSL</option>
                                        <option value="TLS">TLS</option>
                                    </select>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="smtp_username">{{ $t('SMTP Username') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="smtp_username"
                                            v-model="frontend.smtp_username"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="smtp_password">{{ $t('SMTP Password') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="smtp_password"
                                            v-model="frontend.smtp_password"
                                            type="password"
                                            placeholder="••••••••"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                    </div>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        {{ $t('Leave empty to keep the current password') }}.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <span class="inline-flex gap-2">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md"
                                to="/dashboard/admin/frontends"
                            >
                                {{ $t('Close') }}
                            </router-link>
                            <button
                                v-if="frontend.smtp_server"
                                :disabled="testingSmtp"
                                class="btn btn-white shadow-sm rounded-md"
                                type="button"
                                @click="testSmtpConnection"
                            >
                                {{ testingSmtp ? $t('Testing...') : $t('Test connection') }}
                            </button>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Edit frontend') }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>
        <!-- Delete modal -->
        <div v-show="deleteFrontendModal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    duration="300"
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="deleteFrontendModal" class="fixed inset-0 transition-opacity">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>
                </transition>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="deleteFrontendModal"
                        aria-labelledby="modal-headline"
                        aria-modal="true"
                        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                        role="dialog"
                    >
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg-vue class="h-6 w-6 pb-1 text-red-600" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 id="modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                                        {{ $t('Delete frontend') }}
                                    </h3>
                                    <div class="mt-2">
                                        <p class="text-sm leading-5 text-gray-500">
                                            {{ $t('Are you sure you want to delete the frontend?') }}
                                            {{ $t('All data will be permanently removed') }}.
                                            {{ $t('Users linked to this frontend will be unlinked') }}.
                                            {{ $t('This action cannot be undone') }}.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                class="btn btn-red mr-2 sm:mr-0"
                                type="button"
                                @click="deleteFrontend"
                            >
                                {{ $t('Delete frontend') }}
                            </button>
                            <button
                                class="btn btn-white mr-0 sm:mr-2"
                                type="button"
                                @click="deleteFrontendModal = false"
                            >
                                {{ $t('Cancel') }}
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "edit",
    metaInfo() {
        return {
            title: this.$i18n.t('Edit frontend')
        }
    },
    data() {
        return {
            loading: true,
            testingSmtp: false,
            deleteFrontendModal: false,
            employees: [],
            frontend: {
                name: null,
                url: null,
                api_key: '',
                first_receiver_id: null,
                smtp_email: null,
                smtp_server: null,
                smtp_port: null,
                smtp_encryption: 'NO',
                smtp_username: null,
                smtp_password: null,
                sender_name: null,
            },
        }
    },
    mounted() {
        this.getEmployees();
    },
    methods: {
        testSmtpConnection() {
            const self = this;
            self.testingSmtp = true;
            axios.post('api/dashboard/admin/settings/test-smtp', {
                host: self.frontend.smtp_server,
                port: self.frontend.smtp_port,
                username: self.frontend.smtp_username,
                password: self.frontend.smtp_password,
                encryption: self.frontend.smtp_encryption === 'NO' ? '' : self.frontend.smtp_encryption.toLowerCase(),
                from_address: self.frontend.smtp_email,
                from_name: self.frontend.sender_name,
            }).then(function (response) {
                self.testingSmtp = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: response.data.message,
                    type: 'success'
                });
            }).catch(function (error) {
                self.testingSmtp = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: error.response && error.response.data ? error.response.data.message : self.$i18n.t('SMTP connection failed').toString(),
                    type: 'error'
                });
            });
        },
        saveFrontend() {
            const self = this;
            self.loading = true;
            axios.put('api/dashboard/admin/frontends/' + self.$route.params.id, self.frontend).then(function (response) {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
                self.frontend = response.data.frontend;
            }).catch(function () {
                self.loading = false;
            });
        },
        getEmployees() {
            const self = this;
            axios.get('api/dashboard/admin/frontends/employees').then(function (response) {
                self.employees = response.data;
                self.getFrontend();
            }).catch(function () {
                self.loading = false;
            });
        },
        getFrontend() {
            const self = this;
            self.loading = true;
            axios.get('api/dashboard/admin/frontends/' + self.$route.params.id).then(function (response) {
                self.frontend = response.data;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        regenerateApiKey() {
            const self = this;
            self.loading = true;
            axios.post('api/dashboard/admin/frontends/' + self.$route.params.id + '/regenerate-api-key').then(function (response) {
                self.frontend = response.data.frontend;
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        },
        deleteFrontend() {
            const self = this;
            axios.delete('api/dashboard/admin/frontends/' + self.$route.params.id).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/admin/frontends');
            }).catch(function () {
                self.deleteFrontendModal = false;
            });
        },
    }
}
</script>
